import { defineMessages, useIntl } from 'react-intl';

import Suspense from '@atlaskit/react-ufo/suspense';

import { LoadingSpinner } from '@/components/global-nav/user-profile-new/src/components/spinner';
import {
	PriceFinderMessages,
	ProductPlan,
} from '@/components/wpl/pricing/src/price-finder/constants';
import PriceFinderSection from '@/components/wpl/pricing/src/price-finder/price-finder';
import { PriceFinderContextProvider } from '@/components/wpl/pricing/src/price-finder/price-finder-context.tsx';
import { UserLocale } from '@/utils/useGeoCurrency.ts';

import { AppPricingData } from '../../../../contentful/product-catalog';
import ActiveWhiteboards from '../expander-contents/confluence/active-whiteboards';
import AdminControlsInsights from '../expander-contents/confluence/admin-controls-insights';
import AtlassianIntelligence from '../expander-contents/confluence/atlassian-intelligence';
import PerUserAutomation from '../expander-contents/confluence/automation-per-user';
import {
	FreeAutomationRuleRuns,
	StandardAutomationRuleRuns,
} from '../expander-contents/confluence/automation-rule-runs.tsx';
import Databases from '../expander-contents/confluence/databases.tsx';
import DynamicIntranet from '../expander-contents/confluence/dynamic-intranet.tsx';
import ExternalCollaboration from '../expander-contents/confluence/external-collaboration';
import PagePermissions from '../expander-contents/confluence/page-permissions';
import PremiumSupport from '../expander-contents/confluence/premium-support';
import RegionalSupport from '../expander-contents/confluence/regional-support';
import SupportAppsIntegrations from '../expander-contents/confluence/support-apps-integrations';
import SupportAtlassianCommunity from '../expander-contents/confluence/support-atlassian-community';
import Templates from '../expander-contents/confluence/templates';
import UnlimitedPages from '../expander-contents/confluence/unlimited-pages';
import UnlimitedSpaces from '../expander-contents/confluence/unlimited-spaces';
import UnlimitedWhiteboards from '../expander-contents/confluence/unlimited-whiteboards';

export type ConfluencePriceFinder = {
	pricing?: AppPricingData;
	countryCode?: UserLocale;
	v2?: boolean;
	shouldAddDiscount?: boolean;
};

export const ConfluencePriceFinder = (props: ConfluencePriceFinder) => {
	const intl = useIntl();

	const newTagText = intl.formatMessage({
		id: 'ConfluencePricingPage.priceFinder.newTagText',
		defaultMessage: 'New',
	});

	const freePlanFeatureMessages = [
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.unlimitedPages.title',
					defaultMessage: 'Unlimited pages',
				}),
				content: UnlimitedPages,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.unlimitedSpaces.title',
					defaultMessage: 'Unlimited spaces',
				}),
				content: UnlimitedSpaces,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.databases.title',
					defaultMessage: 'Organize and connect work with databases',
				}),
				content: Databases,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.templates.title',
					defaultMessage: 'Templates for Marketing, Product, Program Management and more',
				}),
				content: Templates,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.activeWhiteboards.title',
					defaultMessage: 'Up to 3 active whiteboards per user ',
				}),
				content: ActiveWhiteboards,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.freeAutomationRuleRuns.title',
					defaultMessage: '10 automation rule runs per month ',
				}),
				content: FreeAutomationRuleRuns,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.supportAppsAndIntegrations',
					defaultMessage: 'Support for apps and integrations',
				}),
				content: SupportAppsIntegrations,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.supportFromAtlassianCommunity',
					defaultMessage: 'Support from Atlassian Community',
				}),
				content: SupportAtlassianCommunity,
			},
		},
		{
			featureMessage: intl.formatMessage({
				id: 'ConfluencePricingPageTeam24.features.freeFileStorage',
				defaultMessage: 'Up to 2 GB file storage',
			}),
		},
	];

	const standardPlanFeatureMessages = [
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.externalCollaboration.title',
					defaultMessage: 'External collaboration',
				}),
				content: ExternalCollaboration,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.pagePermissions.title',
					defaultMessage: 'Page permissions',
				}),
				content: PagePermissions,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.standardAutomationRuleRuns.title',
					defaultMessage: '100 automation rule runs per month',
				}),
				content: StandardAutomationRuleRuns,
			},
		},
		{
			featureMessage: intl.formatMessage({
				id: 'ConfluencePricingPageTeam24.features.standardStorage.title',
				defaultMessage: '250 GB of storage',
			}),
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.regionalSupport.title',
					defaultMessage: '9/5 regional support',
				}),
				content: RegionalSupport,
			},
		},
		{
			featureMessage: intl.formatMessage({
				id: 'ConfluencePricingPageTeam24.features.standard150000singlesSite.title',
				defaultMessage: 'Up to 150,000 users on a single site',
			}),
		},
	];

	const premiumPlanFeatureMessages = [
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.aiPowered.title',
					defaultMessage: 'AI-powered creation and collaboration',
				}),
				content: AtlassianIntelligence,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.dynamicIntranet.title',
					defaultMessage: 'Dynamic intranet',
				}),
				content: DynamicIntranet,
			},
			lozengeText: newTagText,
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.perUserAutomation.title',
					defaultMessage: 'Per user automation limits (1000 per month)',
				}),
				content: PerUserAutomation,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.unlimitedWhiteboards.title',
					defaultMessage: 'Unlimited whiteboards',
				}),
				content: UnlimitedWhiteboards,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.adminControlsInsights.title',
					defaultMessage: 'Admin controls and insights',
				}),
				content: AdminControlsInsights,
			},
		},
		{
			featureMessage: intl.formatMessage({
				id: 'ConfluencePricingPageTeam24.features.unlimitedStorage.title',
				defaultMessage: 'Unlimited storage',
			}),
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'ConfluencePricingPageTeam24.features.premiumSupport.title',
					defaultMessage: '24/7 support for critical issues',
				}),
				content: PremiumSupport,
			},
		},
		{
			featureMessage: intl.formatMessage({
				id: 'ConfluencePricingPageTeam24.features.premiumSla.title',
				defaultMessage: '99.9% uptime SLA',
			}),
		},
	];

	const confluenceProductPlans: ProductPlan[] = [
		{
			planName: 'Free',
			offeringSku: 'confluence-free',
			description: intl.formatMessage({
				id: 'ConfluencePricingPageTeam24.priceFinder.freeDescription',
				defaultMessage: 'Free forever for 10 users',
			}),
			recommendationLimit: 0,
			signupLimit: 150000,
			signupURLSuffix: '?bundle=confluence&edition=free&signupSource=pricing',
			planFeatureMessages: freePlanFeatureMessages,
		},
		{
			planName: 'Standard',
			offeringSku: 'confluence-standard',
			description: intl.formatMessage({
				id: 'ConfluencePricingPageTeam24.priceFinder.standardDescription',
				defaultMessage: 'Everything you need to get started',
			}),
			recommendationLimit: 10,
			signupLimit: 150000,
			signupURLSuffix: '?bundle=confluence&edition=standard',
			planFeatureMessages: standardPlanFeatureMessages,
		},
		{
			planName: 'Premium',
			offeringSku: 'confluence-premium',
			description: intl.formatMessage({
				id: 'ConfluencePricingPageTeam24.priceFinder.premiumDescription',
				defaultMessage: 'Connect work across teams',
			}),
			recommendationLimit: 250,
			signupLimit: 150000,
			signupURLSuffix: '?bundle=confluence&edition=premium',
			planFeatureMessages: premiumPlanFeatureMessages,
		},
	];

	const messages: PriceFinderMessages = defineMessages({
		headerText: {
			id: 'ConfluencePricingPageTeam24.priceFinder.headerText',
			defaultMessage: 'Build Your <blue>Source of Truth</blue>',
		},
		unitCountInputTextRedesign: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputTextRedesign',
			defaultMessage: 'Team size:',
		},
		unitCountInputDirectBuy: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputDirectBuy',
			defaultMessage: "What's your team size?",
		},
		unitCountInputTerm: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputTerm',
			defaultMessage: 'users',
		},
		billingCycleText: {
			id: 'ConfluencePricingPage.priceFinder.billingCycleText',
			defaultMessage: 'Billing cycle:',
		},
		unitCountInputText: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputText',
			defaultMessage: 'How many users are on your team?',
		},
		unitCountInputTooltip: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputTooltip',
			defaultMessage:
				'A Confluence user is any user who can log in to Confluence, and who exists in User Management',
		},
		monthlyExampleHelpText: {
			id: 'ConfluencePricingPage.priceFinder.monthlyExampleHelpText',
			defaultMessage:
				'Monthly plans give you flexibility as your number of active users changes, so pay for seats as you use them. Remember that the number of seats may change the total price.',
		},
		annualExampleHelpText: {
			id: 'ConfluencePricingPage.priceFinder.annualExampleHelpText',
			defaultMessage:
				'Annual plans give you flexibility and help you prepare for growth. Buy a group of seats at a flat rate now, then fill your empty seats through the year at no extra cost.',
		},
		exampleStandardPlan: {
			id: 'ConfluencePricingPage.priceFinder.exampleStandardPlan',
			defaultMessage: 'Pricing for {unitCount} users on Standard plan',
			description: 'Standard is a plan name and should not be translated',
		},
		cardOverSignupLimit: {
			id: 'ConfluencePricingPage.priceFinder.cardOverSignupLimit',
			defaultMessage: "Your user count exceeds this plan's limit",
		},
		cardEnterpriseMinimumCount: {
			id: 'ConfluencePricingPage.priceFinder.cardEnterpriseMinimumCount',
			defaultMessage:
				'Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above',
			description: 'Enterprise is a plan name and should not be translated',
		},
		cardUnitCountTierRange: {
			id: 'ConfluencePricingPage.priceFinder.cardUnitCountTierRange',
			defaultMessage: 'User tier: {lowerLimit} - {upperLimit}',
		},
		cardMonthlyPerUnitCount: {
			id: 'ConfluencePricingPageTeam24.priceFinder.cardMonthlyPerUnitCount',
			defaultMessage: 'per user / month',
		},
		cardFreeDescription: {
			id: 'ConfluencePricingPage.priceFinder.cardFreeDescription',
			defaultMessage: 'Always free for 10 users',
		},
	});

	return (
		<Suspense interactionName={'conf-price-finder'} fallback={LoadingSpinner}>
			<PriceFinderContextProvider
				dataProductKey="confluence"
				productPlans={confluenceProductPlans}
				defaultUnitCount={150}
				shouldAddDiscount={props.shouldAddDiscount}
				ctaUrlSource="productPlans"
			>
				<PriceFinderSection
					messages={messages}
					serverSideCCPPricing={props.pricing?.ccpPricingData?.confluence}
					countryCode={props.countryCode}
				/>
			</PriceFinderContextProvider>
		</Suspense>
	);
};

ConfluencePriceFinder.displayName = 'ConfluencePriceFinder';

export default ConfluencePriceFinder;
